// c:\Users\willr\printprice\frontend\src\interfaces\user\FileUpload.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext'; // Adjusted path based on context location

// Define acceptable file types for printing - MUST MATCH BACKEND
const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/postscript': ['.eps'],
  'application/eps': ['.eps'],
  'application/x-eps': ['.eps'],
  'image/x-eps': ['.eps'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
  'application/zip': ['.zip'],
  'application/x-indesign': ['.indd'],
};
// Map MIME types to user-friendly extensions for display
const ACCEPTED_EXTENSIONS_DISPLAY = [...new Set(
  Object.values(ACCEPTED_FILE_TYPES)
    .flat()
    .map(ext => ext.substring(1).toUpperCase())
)]
  .sort()
  .join(', ');

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB max file size
const MAILBIGFILE_URL = 'https://www.mailbigfile.com/originzone';

// Add a quoteName prop for clarity
const FileUpload = ({ jobId, onUploadComplete, quoteName }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingExistingFiles, setLoadingExistingFiles] = useState(false);
  const [folderSummary, setFolderSummary] = useState(null);
  const { isAuthenticated, currentUser } = useAuth(); // Removed getSessionId - using httpOnly cookie now

  const mailBigFileHref = currentUser?.email
    ? `${MAILBIGFILE_URL}/custom-fields/?set_from=${encodeURIComponent(currentUser.email)}`
    : MAILBIGFILE_URL;

  // Load existing files when component mounts or jobId changes
  useEffect(() => {
    if (jobId) {
      loadExistingFiles();
    }
  }, [jobId]);

  const loadExistingFiles = async () => {
    if (!isAuthenticated) return;
    
    setLoadingExistingFiles(true);
    try {
      const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';
      
      // Use withCredentials to send httpOnly cookie instead of Authorization header
      const response = await axios.get(`${UPLOAD_API_URL}/api/folder/files/${jobId}`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setExistingFiles(response.data.files);
        setFolderSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error loading existing files:', error);
      // Don't show error to user for this - just log it
    } finally {
      setLoadingExistingFiles(false);
    }
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      const newErrors = rejectedFiles.map(file => {
        if (file.errors[0].code === 'file-too-large') {
          return `${file.file.name} is too large. Max size is 100MB. Please use the MailBigFile option for larger files.`;
        }
        if (file.errors[0].code === 'file-invalid-type') {
          return `${file.file.name} has an unsupported file type. Accepted types: ${ACCEPTED_EXTENSIONS_DISPLAY}.`;
        }
        return `${file.file.name} could not be uploaded: ${file.errors[0].message}`;
      });
      setErrors(prev => [...prev, ...newErrors]);
    }

    // Process accepted files
    setFiles(prev => [...prev, ...acceptedFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file) // Optional: for image previews if needed later
      }))]);
    // Clear general drop errors when new files are accepted
    setErrors(prev => prev.filter(err => !err.includes('could not be uploaded') && !err.includes('is too large') && !err.includes('unsupported file type')));
  }, [ACCEPTED_EXTENSIONS_DISPLAY]); // Add dependency

  // Cleanup object URLs on unmount
  React.useEffect(() => () => {
    files.forEach(file => URL.revokeObjectURL(file.preview));
  }, [files]);


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES, // Use the constant here
    maxSize: MAX_FILE_SIZE
  });

  const uploadFiles = async () => {
    if (!isAuthenticated) {
      setErrors(prev => [...prev, 'Authentication error: Please log in again.']);
      return;
    }

    // Session token is now in httpOnly cookie - no need to check localStorage

    setUploading(true);
    setErrors([]); // Clear previous errors before new upload attempt
    let uploadSuccessful = true; // Track if all uploads succeed

    for (const file of files) {
      console.log("Current jobId before appending:", jobId);  
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', jobId);

      // Reset progress for this file before starting
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

      try {
        const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';
        if (!UPLOAD_API_URL) {
            console.error("ERROR: REACT_APP_UPLOAD_API_URL is not defined in your .env file!");
            // Optionally set an error state and return
            setErrors(prev => [...prev, "Upload service URL is not configured."]);
            setUploading(false);
            return;
        }
        console.log("Attempting to upload to:", `${UPLOAD_API_URL}/api/upload`); // Verify the URL

        // Use withCredentials to send httpOnly cookie instead of Authorization header
        await axios.post(`${UPLOAD_API_URL}/api/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0; // Handle cases where total is not available initially
            setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
          }
        });
         // Mark file as complete in progress
         setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));

      } catch (error) {
        uploadSuccessful = false;
        const errorMsg = error.response?.data?.detail || error.message || 'An unknown error occurred';
        let displayError = `Failed to upload ${file.name}: ${errorMsg}`; // Default detailed error
        // Check specifically for the Graph API permission error if possible
        setErrors(prev => [...prev, `Failed to upload ${file.name}: ${errorMsg}`]);
        // Reset progress for the failed file
        setUploadProgress(prev => {
          const newProgress = {...prev};
          delete newProgress[file.name];
          return newProgress;
        });

        if (error.response && error.response.status === 401) {
          // Prioritize showing the auth error and stop further uploads
          setErrors(['Authentication error: Please log in again.']);
          break;
        }
      }
    }

    setUploading(false);

    // Call completion callback with success status
    if (onUploadComplete) {
        onUploadComplete(uploadSuccessful);
    }

    // After successful upload, refresh the file list
    if (uploadSuccessful) {
      await loadExistingFiles();
    }

    // Only clear files if all uploads were successful
    if (uploadSuccessful) {
      setFiles([]);
      setUploadProgress({});
    }
  };

  const removeFile = (fileName) => {
    setFiles(files.filter(file => file.name !== fileName));
    setUploadProgress(prev => {
      const newProgress = {...prev};
      delete newProgress[fileName];
      return newProgress;
    });
     // Optional: Remove specific error related to this file
     setErrors(prev => prev.filter(err => !err.includes(fileName)));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType, isJobsheet) => {
    if (isJobsheet) return '📄';
    if (fileType.includes('PDF')) return '📄';
    if (fileType.includes('Image')) return '🖼️';
    if (fileType.includes('Vector')) return '🎨';
    return '📁';
  };

  return (
    // Added padding for use within a modal
    <div className="p-3 md:p-4">
      {/* Existing Files Section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-medium text-gray-800">Files in Folder</h4>
          <button
            onClick={loadExistingFiles}
            disabled={loadingExistingFiles}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          >
            {loadingExistingFiles ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {loadingExistingFiles ? (
          <div className="text-center py-4 text-gray-500">Loading files...</div>
        ) : existingFiles.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-4">
            {/* Summary */}
            {folderSummary && (
              <div className="mb-3 p-3 bg-white rounded border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {folderSummary.total_files} file{folderSummary.total_files !== 1 ? 's' : ''} 
                    ({folderSummary.total_size_formatted})
                  </span>
                  <div className="flex space-x-4 text-xs">
                    <span className={`px-2 py-1 rounded ${folderSummary.has_jobsheet ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {folderSummary.jobsheet_files} Jobsheet{folderSummary.jobsheet_files !== 1 ? 's' : ''}
                    </span>
                    <span className={`px-2 py-1 rounded ${folderSummary.has_artwork ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {folderSummary.artwork_files} Artwork
                    </span>
                  </div>
                </div>
                {folderSummary.ready_for_print && (
                  <div className="mt-2 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      ✓ Ready for Print
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* File List */}
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {existingFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded border hover:bg-gray-50">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <span className="text-lg">{getFileIcon(file.file_type, file.is_jobsheet)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                        {file.is_jobsheet && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Jobsheet
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {file.size_formatted} • {file.file_type} • {formatDate(file.modified_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <a
                      href={file.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View
                    </a>
                    <a
                      href={file.download_url}
                      download={file.name}
                      className="text-green-600 hover:text-green-800 text-sm"
                    >
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
            No files uploaded yet
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="border-t pt-4">
        <h4 className="text-md font-medium text-gray-800 mb-3">Upload New Files</h4>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed p-8 rounded-lg text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer ${
            isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p className="text-blue-600 font-medium">Drop the files here...</p>
          ) : (
            <div>
              <p className="text-gray-700 font-medium">Drag & drop print files here, or click to select files</p>
              <p className="text-gray-500 text-sm mt-2">
                Accepted types: {ACCEPTED_EXTENSIONS_DISPLAY} (Max 100MB). For larger files use the MailBigFile option below.
              </p>
            </div>
          )}
        </div>

        <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between gap-3">
          <p className="text-xs text-blue-800">
            Large files? Use <span className="font-medium">MailBigFile</span> for files over 100MB
          </p>
          <a
            href={files.length > 0 ? undefined : mailBigFileHref}
            target="_blank"
            rel="noopener noreferrer"
            title={files.length > 0 ? "upload or remove current files before using MailBigFile" : "Send large files via MailBigFile"}
            className={`px-3 py-1.5 text-xs rounded whitespace-nowrap transition-colors ${
              files.length > 0
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            onClick={(e) => files.length > 0 && e.preventDefault()}
          >
            MailBigFile
          </a>
        </div>

        {/* Error Display Area */}
        {errors.length > 0 && (
          <div className="mt-3 bg-red-50 border border-red-200 p-3 rounded">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium text-red-800">Upload Errors</h3>
              <button
                className="text-sm text-red-600 font-medium hover:text-red-800"
                onClick={() => setErrors([])}
                aria-label="Clear errors"
              >
                Clear
              </button>
            </div>
            <ul className="list-disc pl-5 space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="text-red-700 text-sm">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* File List and Upload Button */}
        {files.length > 0 && (
          <div className="mt-4">
            <h3 className="font-medium text-lg text-gray-800 mb-2">Files to Upload ({files.length})</h3>
            <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-2 mb-4">
              {files.map(file => (
                <li key={file.name} className="border border-gray-200 rounded-md p-3 flex items-center flex-wrap gap-2">
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate text-sm">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>

                  {/* Progress Bar Area */}
                  {uploadProgress[file.name] !== undefined && !errors.some(err => err.includes(file.name)) && (
                    <div className="w-full sm:w-auto sm:flex-1 order-last sm:order-none mt-2 sm:mt-0 mx-2">
                      <div className="bg-gray-200 rounded-full h-2.5 w-full relative overflow-hidden">
                        <div
                          className={`bg-green-600 h-2.5 rounded-full transition-width duration-300 ease-linear ${uploadProgress[file.name] === 100 ? 'animate-pulse-quick' : ''}`}
                          style={{ width: `${uploadProgress[file.name]}%` }}
                          role="progressbar"
                          aria-valuenow={uploadProgress[file.name]}
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-label={`Upload progress for ${file.name}`}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    className={`ml-auto text-white px-3 py-1 rounded text-xs transition-colors ${
                      uploading ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
                    }`}
                    onClick={() => !uploading && removeFile(file.name)}
                    disabled={uploading}
                    aria-label={`Remove ${file.name}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            {/* Upload Action Button */}
            <button
              className={`px-4 py-2 rounded font-medium text-white w-full sm:w-auto transition-colors ${
                uploading || files.length === 0
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={uploadFiles}
              disabled={uploading || files.length === 0}
            >
              {uploading ? `Uploading ${files.length} file(s)...` : `Upload ${files.length} File(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Add simple pulse animation for completion indication
/* Add to your global CSS:
@keyframes pulse-quick {
  0%, 100% { opacity: 1; }
  50% { opacity: .7; }
}
.animate-pulse-quick {
  animation: pulse-quick 1s ease-in-out;
}
*/

export default FileUpload;
